/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-25
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Add local dependencies.
import { SavedPaymentMethod } from '../../../types/payment';
import { SecurePaymentProvider } from '../providers/SecurePaymentProvider';
// Legacy components removed - demo functionality disabled
// import { PaymentMethodManagement } from '../methods/PaymentMethodManagement';
import { PaymentMethodCard } from '../methods/PaymentMethodCard';
import { PaymentMethodsList } from '../methods/PaymentMethodsList';
// import { AddPaymentMethodModal } from '../methods/AddPaymentMethodModal';

// Mock Data
const mockPaymentMethods: SavedPaymentMethod[] = [
  {
    id: 'pm_1234567890',
    type: 'card',
    card: {
      brand: 'visa',
      last4: '4242',
      exp_month: 12,
      exp_year: 2025,
      funding: 'credit',
      country: 'US',
    },
    billing_details: {
      name: 'John Doe',
      email: 'john@example.com',
      phone: '+1234567890',
      address: {
        line1: '123 Main St',
        line2: 'Apt 4B',
        city: 'New York',
        state: 'NY',
        postal_code: '10001',
        country: 'US',
      },
    },
    created: Date.now() / 1000,
    customer: 'cus_1234567890',
    livemode: false,
    metadata: {},
    isDefault: true,
    nickname: 'Primary Card',
    lastUsed: new Date(),
  },
  {
    id: 'pm_0987654321',
    type: 'card',
    card: {
      brand: 'mastercard',
      last4: '5555',
      exp_month: 8,
      exp_year: 2024,
      funding: 'credit',
      country: 'US',
    },
    billing_details: {
      name: 'John Doe',
      email: 'john@example.com',
      phone: '+1234567890',
      address: {
        line1: '456 Oak Ave',
        line2: null,
        city: 'Los Angeles',
        state: 'CA',
        postal_code: '90210',
        country: 'US',
      },
    },
    created: Date.now() / 1000 - 86400,
    customer: 'cus_1234567890',
    livemode: false,
    metadata: {},
    isDefault: false,
    nickname: 'Backup Card',
  },
  {
    id: 'pm_1122334455',
    type: 'card',
    card: {
      brand: 'amex',
      last4: '0005',
      exp_month: 3,
      exp_year: 2023, // Expired
      funding: 'credit',
      country: 'US',
    },
    billing_details: {
      name: 'John Doe',
      email: 'john@example.com',
      phone: '+1234567890',
      address: {
        line1: '789 Pine St',
        line2: null,
        city: 'Chicago',
        state: 'IL',
        postal_code: '60601',
        country: 'US',
      },
    },
    created: Date.now() / 1000 - 172800,
    customer: 'cus_1234567890',
    livemode: false,
    metadata: {},
    isDefault: false,
    nickname: 'Expired Card',
  },
];

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`payment-tabpanel-${index}`}
    aria-labelledby={`payment-tab-${index}`}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const PaymentMethodDemo = (): React.JSX.Element => {
  const [tabValue, setTabValue] = useState(0);
  const [paymentMethods, setPaymentMethods] =
    useState<SavedPaymentMethod[]>(mockPaymentMethods);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [compact, setCompact] = useState(false);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Simulate API delay
  const simulateApiCall = async (duration = 1000) => {
    setLoading(true);
    await new Promise<void>(resolve => {
      setTimeout(() => resolve(), duration);
    });
    setLoading(false);
  };

  // Handle editing payment method
  const handleEditPaymentMethod = async (paymentMethod: SavedPaymentMethod) => {
    await simulateApiCall(500);

    setPaymentMethods(prev =>
      prev.map(pm =>
        pm.id === paymentMethod.id ? { ...pm, ...paymentMethod } : pm
      )
    );
  };

  // Handle deleting payment method
  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    await simulateApiCall(500);

    setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
  };

  // Handle setting default payment method
  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    await simulateApiCall(500);

    setPaymentMethods(prev =>
      prev.map(pm => ({
        ...pm,
        isDefault: pm.id === paymentMethodId,
      }))
    );
  };

  // Reset demo data
  const resetDemo = () => {
    setPaymentMethods(mockPaymentMethods);
    setError(null);
    setAddModalOpen(false);
  };

  return (
    <SecurePaymentProvider>
      <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <CreditCardIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Payment Method Management Demo
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={resetDemo}
            variant="outlined"
            size="small"
          >
            Reset Demo
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          This demo showcases the payment method management components. Use test
          card number 4242 4242 4242 4242 with any future expiry date and any
          CVC.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Full Management" />
              <Tab label="Payment Methods List" />
              <Tab label="Individual Cards" />
              <Tab label="Add Payment Method" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">
                Complete Payment Method Management (Backend Integration)
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={compact}
                    onChange={e => setCompact(e.target.checked)}
                  />
                }
                label="Compact Mode"
              />
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              This component now fetches real payment methods from the backend
              and handles all CRUD operations automatically.
            </Alert>
            {/* PaymentMethodManagement component removed - using working version in billing page */}
            <Alert severity="warning">
              PaymentMethodManagement demo temporarily disabled. Use the working
              version in the Billing page instead.
            </Alert>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Payment Methods List Component
            </Typography>
            <PaymentMethodsList
              paymentMethods={paymentMethods}
              onAddPaymentMethod={() => setAddModalOpen(true)}
              onEditPaymentMethod={handleEditPaymentMethod}
              onDeletePaymentMethod={handleDeletePaymentMethod}
              onSetDefaultPaymentMethod={handleSetDefaultPaymentMethod}
              loading={loading}
              error={error}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Individual Payment Method Cards
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {paymentMethods.map(paymentMethod => (
                <PaymentMethodCard
                  key={paymentMethod.id}
                  paymentMethod={paymentMethod}
                  onEdit={handleEditPaymentMethod}
                  onDelete={handleDeletePaymentMethod}
                  onSetDefault={handleSetDefaultPaymentMethod}
                  loading={loading}
                />
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              Add Payment Method Modal
            </Typography>
            <Button
              variant="contained"
              onClick={() => setAddModalOpen(true)}
              startIcon={<CreditCardIcon />}
              size="large"
            >
              Open Add Payment Method Modal
            </Button>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Use test card: 4242 4242 4242 4242, any future expiry, any CVC
            </Typography>
          </TabPanel>
        </Paper>

        {/* AddPaymentMethodModal component removed - using working version in billing page */}
        {addModalOpen && (
          <Alert severity="warning" onClose={() => setAddModalOpen(false)}>
            AddPaymentMethodModal demo temporarily disabled. Use the working
            version in the Billing page instead.
          </Alert>
        )}
      </Box>
    </SecurePaymentProvider>
  );
};

export default PaymentMethodDemo;
